import { FC, useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { DelegatedWallet } from '../../wallets';
import { BifrostService } from '../../services/BifrostService';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme } from '../../context/ThemeContext';

interface DelegatedLoginProps {
  onSuccess: (wallet: any) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  bifrostService: BifrostService;
  initialRevealed?: boolean;
  onSessionExpired?: () => void;
}

const DelegatedLogin: FC<DelegatedLoginProps> = ({ onSuccess, isLoading, setIsLoading, bifrostService, initialRevealed = false, onSessionExpired }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isRevealed, setIsRevealed] = useState(initialRevealed);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const delegatedWalletRef = useRef<DelegatedWallet | null>(null);

  // Theme-specific styles
  const qrBgClass = isDarkMode ? 'bg-[#1D1D1D]' : 'bg-white';
  const textMutedClass = isDarkMode ? 'text-gray-400' : 'text-[#666666]';
  const accentTextClass = isDarkMode ? 'text-[#00CC6A]' : 'text-[#00CC6A]';
  // App login text and icon in dark mode only
  const appLoginTextClass = isDarkMode ? 'text-[#00CC6A]' : 'text-gray-800';
  const appLoginIconClass = isDarkMode ? 'stroke-[#00CC6A]' : 'stroke-currentColor';

  // Add effect to handle initial reveal
  useEffect(() => {
    if (initialRevealed && !sessionId) {
      handleReveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRevealed, sessionId]);

  const clearPolling = () => {
    isPollingRef.current = false;
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
  };

  const formatTime = (seconds: number): string => {
    return `${Math.floor(seconds)}`;
  };

  const handleReveal = async () => {
    setIsRevealed(true);
    setIsLoading(true);
    try {
      // Create a new delegated wallet
      delegatedWalletRef.current = new DelegatedWallet();
      const publicKey = delegatedWalletRef.current.getLocalPublicKey();
      
      // Create a session with the wallet's public key
      const sessionId = await bifrostService.createSession(publicKey);
      
      // Get the session to get expiration time
      const session = await bifrostService.getSession();
      
      setSessionId(sessionId);
      setExpiresAt(session.expiresAt);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to start delegated login:', error);

      // Reset states but keep isRevealed true so user can try again
      setIsLoading(false);
      setSessionId(null);
      setExpiresAt(null);
      clearPolling();
      delegatedWalletRef.current = null;
      
      // Show error toast
      toast.error('Failed to start delegated login. Please try again.');
    }
  };

  const pollForWallet = async () => {
    if (!isPollingRef.current || !sessionId) {
      return;
    }

    try {
      const session = await bifrostService.getSession();
      
      // If we have a token and a wallet instance, set the token and return the wallet
      console.log('🔑 [DELEGATED LOGIN] Session:', session);
      if (session.token && delegatedWalletRef.current) {
        console.log('🔑 [DELEGATED LOGIN] Setting token:', session.token);
        delegatedWalletRef.current.setJwtToken(session.token);
        bifrostService.clearSession();
        clearPolling();
        setIsLoading(false);
        onSuccess(delegatedWalletRef.current);
        return;
      }
      
      // Continue polling if we haven't found a wallet
      if (isPollingRef.current) {
        pollingTimeoutRef.current = setTimeout(pollForWallet, 4000);
      }
    } catch (error: any) {
      if (error.status === 410) {
        clearPolling();
        toast.error('Session expired. Please try again.');
        onSessionExpired?.();
      } else if (isPollingRef.current) {
        pollingTimeoutRef.current = setTimeout(pollForWallet, 4000);
      }
    }
  };

  // Start polling when we have a session ID
  useEffect(() => {
    if (sessionId && !isPollingRef.current) {
      isPollingRef.current = true;
      pollForWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Start/update timer when expiresAt changes
  useEffect(() => {
    const updateTimeRemaining = () => {
      if (!expiresAt) return;
      const now = Math.floor(Date.now() / 1000); // Convert to seconds
      const remaining = Math.max(0, expiresAt - now);
      setTimeRemaining(remaining * 1000); // Convert back to milliseconds for display

      if (remaining === 0) {
        setIsRevealed(false); // Reset to initial state
        clearPolling();
        onSessionExpired?.();
      }
    };

    if (expiresAt) {
      // Clear any existing timer
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      // Start new timer
      countdownRef.current = setInterval(updateTimeRemaining, 1000);
      // Initial update
      updateTimeRemaining();
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [expiresAt, onSessionExpired]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPolling();
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  if (!isRevealed) {
    return (
      <div className="flex flex-col items-center justify-center w-full px-[10px] sm:px-0">
        <div 
          className={`${qrBgClass} rounded-lg py-10 px-5 sm:p-4 flex flex-col items-center cursor-pointer hover:opacity-90 transition-opacity w-full max-w-[280px]`}
          onClick={handleReveal}
        >
          <div className="w-full aspect-square flex items-center justify-center">
            <img src="/qr-placeholder.png" alt="QR code placeholder" className="w-full h-full object-cover rounded-xl" />
          </div>
          <span className="mt-2 text-[14px] text-[#666666] font-medium font-inter opacity-0 select-none">Tap to log in on the same device</span>
        </div>
        <div className="h-[40px] mt-4 flex items-center justify-center">
          <button 
            onClick={handleReveal}
            className={`${appLoginTextClass} hover:text-[#00CC6A] text-[14px] font-medium font-inter flex items-center gap-2`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${appLoginIconClass} lucide lucide-qr-code-icon lucide-qr-code`}>
              <rect width="5" height="5" x="3" y="3" rx="1"/>
              <rect width="5" height="5" x="16" y="3" rx="1"/>
              <rect width="5" height="5" x="3" y="16" rx="1"/>
              <path d="M21 16h-3a2 2 0 0 0-2 2v3"/>
              <path d="M21 21v.01"/>
              <path d="M12 7v3a2 2 0 0 1-2 2H7"/>
              <path d="M3 12h.01"/>
              <path d="M12 3h.01"/>
              <path d="M12 16v.01"/>
              <path d="M16 12h1"/>
              <path d="M21 12v.01"/>
              <path d="M12 21v-1"/>
            </svg>
            Reveal QR code
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full px-[10px] sm:px-0">
      {sessionId ? (
        <>
          <div className={`${qrBgClass} rounded-lg py-10 px-5 sm:p-4 flex flex-col items-center w-full max-w-[280px]`}>
            <a 
              href={`https://app.sourceful.energy/login/delegate?session=${sessionId}&type=link`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center hover:opacity-90 transition-opacity w-full"
            >
              <div className="w-full aspect-square flex items-center justify-center">
                <QRCodeSVG
                  value={`https://app.sourceful.energy/login/delegate?session=${sessionId}&type=qr`}
                  size={232}
                  level="H"
                  includeMargin={false}
                  bgColor={isDarkMode ? "#1D1D1D" : "#ffffff"} 
                  fgColor={isDarkMode ? "#ffffff" : "#000000"}
                />
              </div>
              <span className={`mt-2 text-[14px] ${accentTextClass} font-medium font-inter whitespace-nowrap`}>Scan with camera or Sourceful App</span>
            </a>
          </div>
          <div className="h-[40px] mt-4 flex items-center justify-center">
            {timeRemaining !== null && (
              <div className={`${textMutedClass} text-[14px] font-medium font-inter flex items-center gap-2`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${accentTextClass} lucide lucide-timer-icon lucide-timer`}>
                  <line x1="10" x2="14" y1="2" y2="2"/>
                  <line x1="12" x2="15" y1="14" y2="11"/>
                  <circle cx="12" cy="14" r="8"/>
                </svg>
                QR code resets in: <span className={`font-mono ${accentTextClass} flex items-center gap-1`}>
                  {formatTime(timeRemaining / 1000)} seconds
                </span>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className={`${qrBgClass} rounded-lg py-10 px-5 sm:p-4 flex flex-col items-center w-full max-w-[280px]`}>
            <div className="w-full aspect-square flex items-center justify-center">
              <img src="/qr-placeholder.png" alt="QR code placeholder" className="w-full h-full object-cover rounded-xl" />
            </div>
            <span className="mt-2 text-[14px] text-[#666666] font-medium font-inter opacity-0 select-none whitespace-nowrap">Scan with camera or Sourceful App</span>
          </div>
          <div className="h-[40px] mt-4 flex items-center justify-center">
            <div className={`${textMutedClass} text-[14px] font-medium font-inter`}>Loading QR code...</div>
          </div>
        </>
      )}
    </div>
  );
};

export default DelegatedLogin; 